import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { X, Smartphone, Share, PlusSquare } from "lucide-react";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

export const InstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Detectar iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    setIsIOS(iOS);

    // Detectar se já está instalado
    const standalone = window.matchMedia("(display-mode: standalone)").matches;
    setIsStandalone(standalone);

    // Verificar se usuário já recusou ou instalou
    const dismissed = localStorage.getItem("install-prompt-dismissed");
    
    if (standalone || dismissed) {
      return;
    }

    // Android/Chrome - Capturar evento beforeinstallprompt
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      
      // Mostrar prompt após 3 segundos
      setTimeout(() => setShowPrompt(true), 3000);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);

    // iOS - Mostrar instruções após 3 segundos se não instalado
    if (iOS && !standalone) {
      setTimeout(() => setShowPrompt(true), 3000);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === "accepted") {
      console.log("PWA instalado");
    }

    setDeferredPrompt(null);
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem("install-prompt-dismissed", "true");
  };

  if (isStandalone || !showPrompt) {
    return null;
  }

  return (
    <div className="fixed bottom-4 left-4 right-4 z-50 animate-in slide-in-from-bottom-5">
      <Card className="border-2 border-primary shadow-2xl">
        <CardContent className="p-4">
          <button
            onClick={handleDismiss}
            className="absolute top-2 right-2 text-muted-foreground hover:text-foreground"
            aria-label="Fechar"
          >
            <X className="h-4 w-4" />
          </button>

          <div className="flex items-start gap-4">
            <div className="p-2 rounded-lg bg-primary/10">
              <Smartphone className="h-6 w-6 text-primary" />
            </div>
            
            <div className="flex-1 space-y-2">
              <h3 className="font-semibold text-sm">Instalar Medicação Certa App</h3>
              
              {isIOS ? (
                <div className="text-xs text-muted-foreground space-y-2">
                  <p>Para instalar no seu iPhone/iPad:</p>
                  <ol className="list-decimal list-inside space-y-1">
                    <li className="flex items-center gap-2">
                      <Share className="h-3 w-3" />
                      Toque no ícone de compartilhar
                    </li>
                    <li className="flex items-center gap-2">
                      <PlusSquare className="h-3 w-3" />
                      Role e toque em "Adicionar à Tela de Início"
                    </li>
                    <li>Toque em "Adicionar"</li>
                  </ol>
                </div>
              ) : (
                <>
                  <p className="text-xs text-muted-foreground">
                    Instale o app na sua tela inicial para acesso rápido e receber alarmes de medicação!
                  </p>
                  <Button 
                    size="sm" 
                    onClick={handleInstallClick}
                    className="w-full"
                  >
                    Instalar App
                  </Button>
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
